import {
  type CodeResponse,
  type TokenResponse,
  hasGrantedAllScopesGoogle,
  hasGrantedAnyScopeGoogle,
  useGoogleLogin,
} from '@react-oauth/google';
import { useState } from 'react';

export enum GoogleOAuthFlow {
  AuthCode = 'auth-code',
}

export interface GoogleOAuthParams {
  onSuccess: (response: CodeResponse) => void;
  onError: (error: Pick<CodeResponse, 'error' | 'error_description' | 'error_uri'>) => void;
  flow?: GoogleOAuthFlow;
  scope?: string;
  redirect_uri?: string;
}

export type GoogleOAuthImplicitParams = Omit<GoogleOAuthParams, 'flow' | 'redirectUri'>;

export interface GoogleOAuthAuthCodeParams extends GoogleOAuthParams {
  flow: GoogleOAuthFlow.AuthCode;
  redirect_uri: string;
  scope: string;
}

export interface UseGoogleOAuthReturn {
  login: () => void;
  hasAllAccess: (token: TokenResponse) => boolean;
  hasAnyAccess: (token: TokenResponse) => boolean;
}

export const useGoogleOAuth = ({ onSuccess, onError, flow, scope }: GoogleOAuthParams): UseGoogleOAuthReturn => {
  // TODO: Might not need this, require double check when call `hasAllAccessFunction`
  const [internalScope] = useState<string>(scope ?? '');

  /**
   * default google login with input params of the hooks
   */
  const params: GoogleOAuthParams = {
    onSuccess,
    onError,
    scope,
  };
  let finalParams = {};
  if (flow === GoogleOAuthFlow.AuthCode) {
    finalParams = { ...params, flow };
  }

  const login = useGoogleLogin(finalParams);

  /**
   * check if use has granted all access via consent form
   */
  const hasAllAccess = (tokenResponse: TokenResponse) => {
    if (!internalScope) return false;
    return hasGrantedAllScopesGoogle(tokenResponse, internalScope);
  };

  /**
   * check if use has granted any of permissions via consent form
   */
  const hasAnyAccess = (tokenResponse: TokenResponse) => {
    if (!internalScope) return false;
    return hasGrantedAnyScopeGoogle(tokenResponse, internalScope);
  };

  return { login, hasAllAccess, hasAnyAccess };
};
